import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  IconButton,
  Stack,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { CHART_TYPES, normalizeGrafico } from '../utils/slideSchema';
import { HUD } from '../utils/slideThemes';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: HUD.text,
    '& fieldset': { borderColor: HUD.border },
    '&:hover fieldset': { borderColor: 'rgba(56,189,248,0.35)' },
    '&.Mui-focused fieldset': { borderColor: HUD.accent },
  },
  '& .MuiInputLabel-root': { color: HUD.muted },
};

/**
 * Editor simples de gráficos do slide ativo.
 */
export default function SlideChartEditor({ graficos = [], onChange }) {
  const list = Array.isArray(graficos) ? graficos : [];

  const update = (index, patch) => {
    const next = list.map((g, i) => (i === index ? { ...g, ...patch } : g));
    onChange(next.map((g, i) => normalizeGrafico(g, i)).filter(Boolean));
  };

  const remove = (index) => {
    onChange(list.filter((_, i) => i !== index).map((g, i) => normalizeGrafico(g, i)).filter(Boolean));
  };

  const add = () => {
    const g = normalizeGrafico({
      titulo: 'Novo gráfico',
      tipo: 'bar',
      labels: ['A', 'B', 'C'],
      valores: [10, 20, 15],
    });
    onChange([...list, g]);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="overline" sx={{ color: HUD.muted, letterSpacing: 1 }}>
          Gráficos
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={add}
          sx={{ textTransform: 'none', color: HUD.accent }}
        >
          Adicionar
        </Button>
      </Box>

      {!list.length && (
        <Typography variant="caption" sx={{ color: HUD.subtle }}>
          Nenhum gráfico. Adicione um ou peça na geração com IA (“inclua gráfico de barras…”).
        </Typography>
      )}

      {list.map((g, index) => (
        <Box
          key={g.id || index}
          sx={{
            p: 1.25,
            borderRadius: 1.5,
            border: `1px solid ${HUD.border}`,
            bgcolor: HUD.bgElevated,
          }}
        >
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                fullWidth
                label="Título"
                value={g.titulo || ''}
                onChange={(e) => update(index, { titulo: e.target.value })}
                sx={fieldSx}
              />
              <IconButton size="small" onClick={() => remove(index)} sx={{ color: HUD.danger }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
            <FormControl size="small" fullWidth sx={fieldSx}>
              <InputLabel>Tipo</InputLabel>
              <Select
                label="Tipo"
                value={g.tipo || 'bar'}
                onChange={(e) => update(index, { tipo: e.target.value })}
              >
                {CHART_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              fullWidth
              label="Labels (vírgula)"
              value={(g.labels || []).join(', ')}
              onChange={(e) =>
                update(index, {
                  labels: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                })
              }
              sx={fieldSx}
            />
            <TextField
              size="small"
              fullWidth
              label="Valores (vírgula)"
              value={(g.valores || []).join(', ')}
              onChange={(e) =>
                update(index, {
                  valores: e.target.value
                    .split(',')
                    .map((s) => Number(String(s).trim()))
                    .filter((n) => !Number.isNaN(n)),
                })
              }
              sx={fieldSx}
            />
          </Stack>
        </Box>
      ))}
    </Box>
  );
}
